import { supabase } from '../lib/supabase';

const SUPABASE_URL = import.meta.env?.VITE_SUPABASE_URL;

/**
 * Get auth token for edge function calls.
 */
async function getAuthToken() {
  const { data: { session } } = await supabase.auth.getSession();
  return session?.access_token || '';
}

/**
 * Call the razorpay-checkout edge function.
 */
async function callRazorpayCheckout(body) {
  const token = await getAuthToken();
  const anonKey = import.meta.env?.VITE_SUPABASE_ANON_KEY;

  const res = await fetch(`${SUPABASE_URL}/functions/v1/razorpay-checkout`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      'apikey': anonKey,
    },
    body: JSON.stringify(body),
  });

  const data = await res.json();
  if (!res.ok) throw new Error(data?.error || data?.message || 'Request failed');
  return data;
}

/**
 * Get user's subscription tier, usage, and limits.
 */
export async function getUserSubscription() {
  try {
    const data = await callRazorpayCheckout({ action: 'usage' });
    return data;
  } catch (error) {
    // Fallback: read from user_profiles directly
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return { tier: 'free', usage: {}, limits: {}, daysUntilReset: 30 };

    const { data: profile } = await supabase
      .from('user_profiles')
      .select('subscription_tier, subscription_status')
      .eq('id', user.id)
      .single();

    return {
      tier: profile?.subscription_tier || 'free',
      status: profile?.subscription_status || 'free',
      usage: { ai_generations: 0, publishes: 0 },
      limits: {},
      daysUntilReset: 30,
    };
  }
}

/**
 * Open Razorpay checkout popup for subscription.
 * Unlike Stripe (redirect), Razorpay opens a popup on the same page.
 *
 * @param {string} planId - Razorpay Plan ID (e.g., 'plan_xxx')
 * @param {object} userInfo - { email, name } for prefilling checkout
 * @returns {Promise<object>} - { success, tier } on successful payment
 */
export async function openRazorpayCheckout(planId, userInfo = {}) {
  // Step 1: Create subscription on server
  const { subscriptionId, keyId } = await callRazorpayCheckout({
    action: 'subscribe',
    planId,
  });

  // Step 2: Open Razorpay popup
  return new Promise((resolve, reject) => {
    // Ensure Razorpay script is loaded
    if (!window.Razorpay) {
      reject(new Error('Payment service is loading. Please try again.'));
      return;
    }

    const options = {
      key: keyId,
      subscription_id: subscriptionId,
      name: 'POSTRequest',
      description: 'Pro Plan Subscription',
      image: '/favicon.svg',
      handler: async (response) => {
        try {
          // Step 3: Verify payment on server
          const result = await callRazorpayCheckout({
            action: 'verify',
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_subscription_id: response.razorpay_subscription_id,
            razorpay_signature: response.razorpay_signature,
          });
          resolve(result);
        } catch (err) {
          reject(err);
        }
      },
      prefill: {
        name: userInfo.name || '',
        email: userInfo.email || '',
      },
      theme: {
        color: '#f97316', // orange-500 matching our brand
      },
      modal: {
        ondismiss: () => {
          reject(new Error('Payment cancelled'));
        },
      },
    };

    const rzp = new window.Razorpay(options);
    rzp.on('payment.failed', (response) => {
      reject(new Error(response?.error?.description || 'Payment failed. Please try again.'));
    });
    rzp.open();
  });
}

/**
 * Cancel the user's subscription at cycle end.
 */
export async function cancelSubscription() {
  return callRazorpayCheckout({ action: 'cancel' });
}

/**
 * Get subscription status from Razorpay.
 */
export async function getSubscriptionStatus() {
  return callRazorpayCheckout({ action: 'status' });
}

/**
 * Check if user can use a feature (client-side hint, server enforces).
 */
export function canUseFeature(feature, subscription) {
  if (!subscription) return false; // Deny by default until subscription loads
  const tier = subscription.tier || 'free';

  if (tier === 'pro' || tier === 'enterprise') return true;

  const proOnlyFeatures = ['image_upload', 'analytics'];
  if (proOnlyFeatures.includes(feature)) return false;

  const quotaMap = {
    ai_generate: { usageKey: 'ai_generations', limitKey: 'ai_generations_per_month' },
    publish: { usageKey: 'publishes', limitKey: 'publishes_per_month' },
    schedule: { usageKey: 'scheduled_posts', limitKey: 'scheduled_posts_per_month' },
  };

  const quota = quotaMap[feature];
  if (!quota) return true;

  const used = subscription.usage?.[quota.usageKey] ?? 0;
  const limit = subscription.limits?.[quota.limitKey] ?? 5;
  return limit === -1 || used < limit;
}

/**
 * Get usage display string.
 */
export function getUsageDisplay(feature, subscription) {
  if (!subscription) return '';
  const tier = subscription.tier || 'free';
  if (tier === 'pro') return 'Unlimited';

  const quotaMap = {
    ai_generate: { usageKey: 'ai_generations', limitKey: 'ai_generations_per_month', label: 'generations' },
    publish: { usageKey: 'publishes', limitKey: 'publishes_per_month', label: 'publishes' },
    schedule: { usageKey: 'scheduled_posts', limitKey: 'scheduled_posts_per_month', label: 'scheduled posts' },
  };

  const quota = quotaMap[feature];
  if (!quota) return '';

  const used = subscription.usage?.[quota.usageKey] ?? 0;
  const limit = subscription.limits?.[quota.limitKey] ?? 5;
  if (limit === -1) return 'Unlimited';
  return `${used}/${limit} ${quota.label} used`;
}
